import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { PRICE_EUR, PRICE_LABEL_FULL } from '../lib/pricing'
import { DiceFive as Dice, ArrowRight, Lightning, Check, X } from '@phosphor-icons/react'
import { useAuth } from '../auth'
import { formatApiError, setEmailPrefs, createCheckoutSession } from '../api'

interface Props {
  mode: 'login' | 'signup'
}

export default function AuthPage({ mode }: Props) {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo = (location.state as any)?.from || '/app'

  // Redirect already-logged-in users immediately
  useEffect(() => {
    if (auth.user) navigate(redirectTo, { replace: true })
  }, [auth.user, navigate, redirectTo])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [wantsDigest, setWantsDigest] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [showPricingNudge, setShowPricingNudge] = useState(false)
  const [upgrading, setUpgrading] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      if (mode === 'login') {
        await auth.login(email, password)
        navigate(redirectTo, { replace: true })
      } else {
        await auth.register(email, password)
        if (wantsDigest) {
          try { await setEmailPrefs({ wants_weekly_digest: true, wants_outcome_emails: true }) } catch { /* non-blocking */ }
        }
        // Show pricing nudge before sending to app
        setShowPricingNudge(true)
        return
      }
    } catch (e: any) {
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  // Google Sign-In is shown only when VITE_GOOGLE_CLIENT_ID is configured.
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
  const googleEnabled = !!GOOGLE_CLIENT_ID

  const handleGoogleSignIn = () => {
    if (!window.google?.accounts?.id) {
      setErr('Google Sign-In failed to load. Please refresh and try again.')
      return
    }
    setBusy(true)
    setErr(null)
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID!,
      callback: async ({ credential }) => {
        try {
          await auth.googleSignIn(credential)
          navigate(redirectTo, { replace: true })
        } catch (e: any) {
          setErr(formatApiError(e?.response?.data?.detail) || e?.message || 'Google sign in failed')
        } finally {
          setBusy(false)
        }
      },
      cancel_on_tap_outside: true,
    })
    window.google.accounts.id.prompt((notification) => {
      // One Tap was suppressed (e.g. user closed it) — fall back to popup
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        setBusy(false)
      }
    })
  }

  const handleAppleSignIn = async () => {
    setBusy(true)
    setErr(null)
    try {
      await auth.appleSignIn()
      navigate(redirectTo, { replace: true })
    } catch (e: any) {
      // Suppress popup-closed — user cancelled intentionally
      if (e?.error === 'popup_closed_by_user') return
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || 'Apple sign in failed')
    } finally {
      setBusy(false)
    }
  }

  const handleUpgradeNow = async () => {
    setUpgrading(true)
    try {
      const { url, already_premium } = await createCheckoutSession()
      if (already_premium) { navigate(redirectTo, { replace: true }); return }
      window.location.href = url
    } catch {
      navigate(redirectTo, { replace: true })
    }
  }

  const isLogin = mode === 'login'

  // Post-signup pricing nudge interstitial
  if (showPricingNudge) {
    return (
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center p-4">
        <div className="anim-pop max-w-sm w-full card-brutal p-8 space-y-6">
          <div className="text-center space-y-1">
            <p className="text-3xl">🎉</p>
            <h2 className="font-heading font-black text-2xl tracking-tight">You're in!</h2>
            <p className="text-sm text-ink-muted">While we set up your account, one quick thing:</p>
          </div>

          {/* Free tier */}
          <div className="rounded-xl border-2 border-edge p-4 bg-muted/30 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-ink-muted">Free</p>
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-ink-muted shrink-0" /> Up to 3 options per decision</p>
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-ink-muted shrink-0" /> 1,000 simulations per run</p>
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-ink-muted shrink-0" /> AI suggestions + sharing</p>
            </div>
          </div>

          {/* Premium tier */}
          <div className="rounded-xl border-2 border-primary bg-primary/5 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-primary-ink">Premium</p>
              <span className="font-heading font-black text-xl">{PRICE_EUR}</span>
            </div>
            <p className="text-xs font-bold text-primary-ink">One-time payment, lifetime access</p>
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-primary-ink shrink-0" /> <strong>Up to 10 options</strong> per decision</p>
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-primary-ink shrink-0" /> <strong>10,000 simulations</strong> per run</p>
              <p className="flex items-center gap-2"><Check size={14} weight="bold" className="text-primary-ink shrink-0" /> Everything in Free</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleUpgradeNow}
              disabled={upgrading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lightning size={16} weight="fill" />
              {upgrading ? 'Opening checkout…' : PRICE_LABEL_FULL}
            </button>
            <button
              onClick={() => navigate(redirectTo, { replace: true })}
              className="w-full text-center text-sm text-ink-muted hover:text-ink font-bold py-2 transition-colors inline-flex items-center justify-center gap-1"
            >
              <X size={12} weight="bold" /> Start with free plan
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <Helmet>
      <title>{mode === 'signup' ? 'Create account — LifeOdds' : 'Sign in — LifeOdds'}</title>
      <meta name="description" content="Free forever. Create an account to save your decisions, sync across devices, and unlock AI-powered analysis." />
    </Helmet>
    <div className="min-h-[calc(100vh-120px)] grid lg:grid-cols-2 gap-8 items-center" data-testid={`auth-page-${mode}`}>
      {/* Left: form */}
      <div className="anim-rise max-w-md w-full mx-auto lg:mx-0 lg:ml-auto">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary border-2 border-edge grid place-items-center shadow-brutal-sm">
            <Dice size={22} weight="fill" className="text-white" />
          </div>
          <span className="font-heading font-black text-xl">LifeOdds</span>
        </Link>

        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-2">
          {isLogin ? (
            <>Welcome <br /><span className="bg-primary text-white px-2 py-0.5 rounded-xl inline-block -rotate-1 border-2 border-edge shadow-brutal-sm">back.</span></>
          ) : (
            <>Your first<br /><span className="bg-secondary text-white px-2 py-0.5 rounded-xl inline-block rotate-1 border-2 border-edge shadow-brutal-sm">simulation is free.</span></>
          )}
        </h1>
        <p className="text-ink-muted mt-3 mb-6">
          {isLogin
            ? 'Sign in to continue building better decisions.'
            : 'Free forever. Save your decisions and get AI coaching.'}
        </p>

        {/* Apple Sign In — Apple HIG: black bg, white text, Apple logo */}
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={busy}
          data-testid="apple-signin-btn"
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-black text-white font-bold rounded-xl border-2 border-edge shadow-brutal-sm hover:bg-gray-800 hover:-translate-y-0.5 hover:shadow-brutal transition-all disabled:opacity-50"
        >
          <svg width="17" height="17" viewBox="0 0 814 1000" fill="white" aria-hidden="true">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.8 10.6 251.2 10.6 108.2c0-105 91-160.8 170.5-160.8 80.5 0 141.4 54.5 173.4 54.5 30.8 0 101.5-58.1 196.2-58.1 31.9 0 122.8 5.1 189.6 64.7zm-134.5-55.5c-7.3-36.6-26.8-73.3-56.8-102.2-30-28.9-65.3-44.8-100.8-44.8-4.5 0-9 .3-13.5.7 2.7 37.9 17.6 75.6 46.5 103.8 28.9 28.2 63.4 44.8 100.8 44.8 7.9 0 15.8-.9 23.8-2.3z"/>
          </svg>
          Sign in with Apple
        </button>

        {/* Google Sign-In — shown only when VITE_GOOGLE_CLIENT_ID is set */}
        {googleEnabled && (
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={busy}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-gray-900 font-bold rounded-xl border-2 border-edge shadow-brutal-sm hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-brutal transition-all disabled:opacity-50"
          >
            {/* Google 'G' logo — official colours */}
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Sign in with Google
          </button>
        )}

        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-edge" />
          <span className="text-xs text-ink-muted font-bold uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-edge" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="auth-email-input" className="label-bold mb-1 block">Email</label>
            <input
              id="auth-email-input" data-testid="auth-email-input"
              type="email"
              required
              className="input-brutal w-full"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="auth-password-input" className="label-bold mb-1 block">Password</label>
            <input
              id="auth-password-input" data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              className="input-brutal w-full"
              placeholder="at least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {err && (
            <div className="p-3 rounded-xl bg-primary/10 border-2 border-primary text-sm font-bold text-primary-ink" role="alert" data-testid="auth-error">
              {err}
            </div>
          )}

          {!isLogin && (
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="digest-optin-row">
              <input
                type="checkbox"
                checked={wantsDigest}
                onChange={(e) => setWantsDigest(e.target.checked)}
                data-testid="digest-optin-checkbox"
                className="mt-1 w-4 h-4 accent-primary"
              />
              <span className="text-sm text-ink-muted">
                Email me a weekly digest of the most-forked decisions on LifeOdds.{' '}
                <span className="text-ink-muted">(unsubscribe anytime)</span>
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid={isLogin ? 'login-submit-btn' : 'signup-submit-btn'}
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {busy ? '…' : (isLogin ? 'Sign in' : 'Create account')}
            <ArrowRight size={16} weight="bold" />
          </button>
        </form>

        <div className="mt-6 text-sm text-ink-muted">
          {isLogin ? (
            <>
              No account?{' '}
              <Link to="/signup" className="text-primary-ink font-bold underline underline-offset-2" data-testid="goto-signup-link">
                Sign up
              </Link>
              {' · '}
              <Link to="/forgot-password" className="text-ink-muted hover:text-primary-ink underline underline-offset-2" data-testid="goto-forgot-link">
                Forgot password?
              </Link>
            </>
          ) : (
            <>
              Already have one?{' '}
              <Link to="/login" className="text-primary-ink font-bold underline underline-offset-2" data-testid="goto-login-link">
                Sign in
              </Link>
            </>
          )}
        </div>

      </div>

      {/* Right: visual flair */}
      <div className="anim-rise hidden lg:block" style={{ animationDelay: '120ms' }}>
        <div className="relative max-w-md mx-auto">
          <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-accent border-2 border-edge animate-float opacity-90" />
          <div className="absolute -bottom-8 -right-4 w-24 h-24 rounded-full bg-secondary border-2 border-edge animate-float opacity-80" style={{ animationDelay: '1.5s' }} />
          <div className="relative card-brutal p-8 bg-gradient-to-br from-surface to-muted">
            <div className="label-bold mb-3">A real LifeOdds result:</div>
            <div className="font-heading text-3xl font-black mb-4">
              🏆 Take the new offer
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="label-bold">Wins</div>
                <div className="font-heading text-3xl font-black mono">78%</div>
              </div>
              <div>
                <div className="label-bold">Avg gain</div>
                <div className="font-heading text-3xl font-black mono text-accent-ink">+€38k</div>
              </div>
            </div>
            <div className="p-3 rounded-xl border-2 border-accent bg-accent/15">
              <div className="text-xs font-bold mb-1">LIFEODDS SUGGESTS</div>
              <p className="text-sm">
                The new offer wins in most futures, but the variance is high. Consider negotiating a longer notice period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
